import React, { useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { Disclosure, Transition } from '@headlessui/react';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { useAppSelector } from 'app/hooks';
import { selectSnowflakeAccount } from 'app/snowflakeSlice';
import { useGetSchemaLevelObjectsQuery } from 'app/createApi';
import ViewSelectorViewItem from './ViewSelectorViewItem';
import type { IView } from 'utils/types';
import { classNames } from 'utils/styleUtils';

const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: '#4b5563',
  color: 'white',
  fontSize: 12,
  zIndex: 30,
  maxWidth: 500,
};

interface TooltipData {
  description: string;
}

interface SchemaItemProps {
  databaseName: string;
  schemaName: string;
  selectedView: IView | undefined;
  setSelectedView: (view: IView) => void;
}

export default function ViewSelectorSchemaItem(props: SchemaItemProps): JSX.Element {
  const snowflakeAccount = useAppSelector(selectSnowflakeAccount);

  const [isOpen, setIsOpen] = useState(false);
  const { data: views, isLoading } = useGetSchemaLevelObjectsQuery(
    {
      account: snowflakeAccount,
      database: props.databaseName,
      schema: props.schemaName,
      object_types: ['view'],
    },
    { skip: !isOpen },
  );

  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } = useTooltip<TooltipData>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });

  return (
    <>
      <Disclosure>
        {({ open }) => (
          <>
            <div className="w-full px-2 relative flex py-1 border border-gray-300 bg-gray-50 rounded-md text-gray-700 cursor-default">
              <div className="min-w-[20px] w-5 mr-1" ref={containerRef}>
                <img className="w-5" src="/static/schema-icon.svg" alt="Schema" />
              </div>
              <div
                className="text-sm truncate mr-2"
                onMouseLeave={() => {
                  hideTooltip();
                }}
                onMouseMove={(event) => {
                  const eventSvgCoords = localPoint(event);
                  showTooltip({
                    tooltipData: { description: props.schemaName },
                    tooltipTop: eventSvgCoords?.y !== undefined ? eventSvgCoords.y + 10 : undefined,
                    tooltipLeft: eventSvgCoords?.x,
                  });
                }}
              >
                {props.schemaName}
              </div>
              <Disclosure.Button className="absolute right-1 w-5 cursor-pointer">
                <ChevronRightIcon
                  onClick={() => setIsOpen(!isOpen)}
                  className={classNames(
                    open ? 'transform rotate-90' : '',
                    'w-5 h-5 ml-auto text-gray-700 transition duration-150 ease-in-out',
                  )}
                />
              </Disclosure.Button>
            </div>

            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacitsy-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Disclosure.Panel className="px-4 space-y-1">
                {!isLoading &&
                  views !== undefined &&
                  views.view !== undefined &&
                  views.view.map((view) => (
                    <ViewSelectorViewItem
                      key={view}
                      databaseName={props.databaseName}
                      schemaName={props.schemaName}
                      viewName={view}
                      selectedView={props.selectedView}
                      setSelectedView={props.setSelectedView}
                    />
                  ))}
                {isLoading && (
                  <div className="w-full h-[30px] border border-gray-300 rounded-md py-1 px-2">
                    <div className="animate-pulse h-2 bg-slate-300 rounded mt-[6px]"></div>
                  </div>
                )}
                {!isLoading && (views?.view === undefined || views.view.length === 0) && (
                  <div className="mb-2 text-sm text-gray-500">No views found</div>
                )}
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
      {tooltipOpen && tooltipData !== undefined && (
        <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          <div className="text-sm">{tooltipData.description}</div>
        </TooltipInPortal>
      )}
    </>
  );
}
